/**
 * TaxYearContext
 *
 * Shared context for selected tax year across all tax pages.
 * Persists selection as user navigates between tax screens.
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import { CURRENT_TAX_YEAR } from "@moonscape/shared";

interface TaxYearContextType {
  selectedTaxYear: number;
  setSelectedTaxYear: (year: number) => void;
}

const TaxYearContext = createContext<TaxYearContextType | undefined>(undefined);

export const TaxYearProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedTaxYear, setSelectedTaxYearState] = useState(CURRENT_TAX_YEAR - 1);

  const setSelectedTaxYear = useCallback((year: number) => {
    setSelectedTaxYearState(year);
  }, []);

  return (
    <TaxYearContext.Provider value={{ selectedTaxYear, setSelectedTaxYear }}>
      {children}
    </TaxYearContext.Provider>
  );
};

export const useTaxYear = (): TaxYearContextType => {
  const context = useContext(TaxYearContext);
  if (!context) {
    throw new Error("useTaxYear must be used within a TaxYearProvider");
  }
  return context;
};

// Optional hook that returns default if not in provider (for gradual migration)
export const useTaxYearOptional = (): TaxYearContextType => {
  const context = useContext(TaxYearContext);
  if (!context) {
    return {
      selectedTaxYear: CURRENT_TAX_YEAR - 1,
      setSelectedTaxYear: () => {},
    };
  }
  return context;
};
